import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Eye, EyeOff, Loader2, Lock } from 'lucide-react';
import { Ambient } from './Ambient';
import { VexuLockup } from './Brand';
const AUTHORIZED_EMAIL = 'zain@vexu.ai';
const AUTHORIZED_PASSWORD = 'Zain@0123';
const EASE = [0.23, 1, 0.32, 1] as const;
interface PresentationLoginProps {
  onAuthenticated: () => void;
}
export function PresentationLogin({
  onAuthenticated
}: PresentationLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (verifying) return;
    setError(null);
    setVerifying(true);
    window.setTimeout(() => {
      const valid = email.trim().toLowerCase() === AUTHORIZED_EMAIL && password === AUTHORIZED_PASSWORD;
      if (valid) {
        onAuthenticated();
        return;
      }
      setVerifying(false);
      setError('Those credentials don’t match an authorized viewer.');
    }, 650);
  };
  const inputClass = 'h-[52px] w-full rounded-xl border border-slate-900/[0.10] bg-white px-[16px] text-[15px] text-slate-900 placeholder:text-slate-400 transition-[border-color,box-shadow] duration-200 ease-out focus:border-vexu focus:outline-none focus:ring-4 focus:ring-vexu/15';
  return <div className="fixed inset-0 flex overflow-hidden bg-white font-sans antialiased" data-id="element-453" data-mp="3788kmm7b1xwg">
      {/* Brand panel */}
      <div className="relative hidden flex-1 flex-col justify-between overflow-hidden border-r border-slate-900/[0.07] px-[72px] py-[64px] lg:flex" data-id="element-454" data-mp="3c11qxeif9ean">
        <Ambient variant="cover" data-id="element-455" data-mp="3lxwonv6vfanv" />

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE
      }} className="relative" data-id="element-456" data-mp="3lyppkfj5f5y3">
          <VexuLockup size={36} wordmarkSize={23} data-id="element-457" data-mp="1qtyq2mpvpqp9" />
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.08
      }} className="relative max-w-[560px]" data-id="element-458" data-mp="1lvx4nv4zdfam">
          <span className="block text-[12px] font-semibold uppercase tracking-[0.34em] text-vexu-600" data-id="element-459" data-mp="1uw3bwza35j1c">
            Executive Briefing
          </span>
          <h1 className="mt-[26px] text-[52px] font-semibold leading-[1.04] tracking-[-0.035em] text-slate-900" data-id="element-460" data-mp="29nc85r8hu2ng">
            The AI Conversational
            <br data-id="element-461" data-mp="2im7er4fg9era" />
            Layer for Telecom
          </h1>
          <p className="mt-[22px] text-[17px] font-light leading-relaxed text-slate-500" data-id="element-462" data-mp="2ekciiecdlrkd">
            One AI technology stack. Multiple telecom-ready experiences.
          </p>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.16
      }} className="relative" data-id="element-463" data-mp="k6b7yw7grg4g">
          <div className="h-px w-full bg-slate-900/[0.10]" data-id="element-464" data-mp="e3q6a7x0k6vu" />
          <div className="flex items-end justify-between pt-[26px]" data-id="element-465" data-mp="e36kuh85n0d2">
            <span className="text-[12px] font-medium uppercase tracking-[0.24em] text-slate-500" data-id="element-466" data-mp="bider8up02f">
              Strategic Technology Partnership
            </span>
            <span className="pb-[1px] text-[11px] font-medium uppercase tracking-[0.24em] text-slate-400" data-id="element-467" data-mp="3mk1f58avzcfj">
              Confidential
            </span>
          </div>
        </motion.div>
      </div>

      {/* Access panel */}
      <div className="flex w-full items-center justify-center px-[32px] lg:w-[560px] lg:px-[72px]" data-id="element-468" data-mp="3djv2fon0ighf">
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: EASE,
        delay: 0.12
      }} className="w-full max-w-[400px]" data-id="element-469" data-mp="3ij7r8exg1xmn">
          <div className="mb-[30px] lg:hidden" data-id="element-470" data-mp="1gqb8e5xalqrz">
            <VexuLockup size={32} wordmarkSize={21} data-id="element-471" data-mp="3ctpn31e2c040" />
          </div>

          <span className="inline-flex items-center gap-[8px] rounded-full border border-slate-900/[0.08] bg-slate-50 px-[12px] py-[5px] text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500" data-id="element-472" data-mp="3crvcoh0f3qe6">
            <Lock className="h-[11px] w-[11px] text-vexu-600" data-id="element-473" data-mp="32xmpm8u5opne" />
            Secure Access
          </span>

          <h2 className="mt-[22px] text-[32px] font-semibold tracking-[-0.03em] text-slate-900" data-id="element-474" data-mp="2tv3lzmijq903">
            Enter the presentation
          </h2>
          <p className="mt-[10px] text-[14.5px] font-light leading-relaxed text-slate-500" data-id="element-475" data-mp="2p3llezkfqafn">
            This briefing is shared with authorized viewers only.
          </p>

          <form onSubmit={handleSubmit} className="mt-[34px] space-y-[18px]" data-id="element-476" data-mp="2f6pl3h4owum6">
            <div data-id="element-477" data-mp="2b4m7omygnfy9">
              <label htmlFor="presentation-email" className="mb-[8px] block text-[12px] font-medium tracking-[0.01em] text-slate-600" data-id="element-478" data-mp="2b540f9lwzc1f">
                Email
              </label>
              <input id="presentation-email" type="email" autoComplete="username" autoFocus value={email} onChange={event => setEmail(event.target.value)} placeholder="name@company.com" aria-invalid={Boolean(error)} className={inputClass} data-id="element-479" data-mp="lmp0rqzigr9i" />
            </div>

            <div data-id="element-480" data-mp="2ytu0r2bgdpw">
              <label htmlFor="presentation-password" className="mb-[8px] block text-[12px] font-medium tracking-[0.01em] text-slate-600" data-id="element-481" data-mp="2v5yxnh83j92">
                Password
              </label>
              <div className="relative" data-id="element-482" data-mp="goxbz5u5xl9j">
                <input id="presentation-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={event => setPassword(event.target.value)} placeholder="••••••••" aria-invalid={Boolean(error)} className={`${inputClass} pr-[48px]`} data-id="element-483" data-mp="gpyl34rmbxom" />
                <button type="button" onClick={() => setShowPassword(prev => !prev)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="absolute right-[12px] top-1/2 grid h-[30px] w-[30px] -translate-y-1/2 place-items-center rounded-lg text-slate-400 transition-colors duration-200 ease-out hover:bg-slate-100 hover:text-slate-600" data-id="element-484" data-mp="10b2bzax1qmq6">
                  {showPassword ? <EyeOff className="h-[16px] w-[16px]" data-id="element-485" data-mp="clpt1jbx30ao" /> : <Eye className="h-[16px] w-[16px]" data-id="element-486" data-mp="cnu90zn3bprb" />}
                </button>
              </div>
            </div>

            {error && <motion.p role="alert" initial={{
            opacity: 0,
            y: -6
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.2,
            ease: EASE
          }} className="text-[13px] font-medium text-red-600" data-id="element-487" data-mp="1o03dee7goxly">
                {error}
              </motion.p>}

            <button type="submit" disabled={verifying} className="group flex h-[52px] w-full items-center justify-center gap-[10px] rounded-xl bg-vexu-600 text-[15px] font-semibold text-white shadow-[0_14px_34px_-14px_rgba(0,153,64,0.7)] transition-[background-color,transform] duration-200 ease-out hover:bg-vexu-700 active:scale-[0.99] disabled:opacity-70" data-id="element-488" data-mp="1risrl9eyg2ri">
              {verifying ? <>
                  <Loader2 className="h-[17px] w-[17px] animate-spin" data-id="element-489" data-mp="2aftyx0vzeeym" />
                  Verifying
                </> : <>
                  Enter Presentation
                  <ArrowRight className="h-[17px] w-[17px] transition-transform duration-200 ease-out group-hover:translate-x-[3px]" data-id="element-490" data-mp="32zk70d56jcac" />
                </>}
            </button>
          </form>

          <div className="mt-[36px] h-px w-full bg-slate-900/[0.08]" data-id="element-491" data-mp="y3orrieeu85m" />
          <p className="mt-[18px] text-[11.5px] font-light leading-relaxed text-slate-400" data-id="element-492" data-mp="t6yairqvd6im">
            Prepared for Zain Telecom by Vexu AI. Access is monitored and
            intended for the named recipients.
          </p>
        </motion.div>
      </div>
    </div>;
}